// ***************************************
// ComponentButton
//
// This component displays and handles a button with an image
//
// Copyright 2015 Tingly Games B.V.
// Last Changed: 26-9-2015
// ***************************************
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { tbMath } from "../../engine/tbMath";
import { tgfSettings } from "../tgfSettings";
import { ComponentBase } from "./ComponentBase";
import { PIXI } from "../../engine/tbPIXI";
import { tbGame } from "../../engine/tbGame";
import { tgfAudio } from "../tgfAudio";
import { tbVector } from "../../engine/tbVector";
import { tbVectorFont } from "../../engine/tbVectorFont";
import { sfx_generalbutton } from "../../code/AssetDefinitions/SoundDefinitions";
import { s_tgf_transparent } from "../../code/AssetDefinitions/ImageDefinitions";
import { tgfDictionary } from "../tgfDictionary";
tgfSettings.componentDefaults.button = {
    image: undefined,
    createFromSlices: false,
    mask: undefined,
    marginLeft: 24,
    marginRight: 24,
    marginTop: 24,
    marginBottom: 24,
    icon: null,
    iconPosition: "left",
    iconAlpha: 1,
    text: "",
    dictionaryKey: "",
    fontDown: null,
    fontUp: undefined,
    splitLines: false,
    fitToBox: true,
    soundDown: undefined,
    soundUp: null,
    toggleIndex: 0,
    toggleCount: 0,
    touchMargin: 0,
    touchMarginBox: null,
    shortcutKey: null,
    anyKey: false,
    reactOnDown: false,
    cancelEvents: true,
    roundPosition: true,
    components: [],
    onClick: null,
    allowedTouchMotion: 10000,
    canClick: true,
    iconDownScale: .9,
    fontDownScale: .95,
    fontUpScale: 1,
    iconUpScale: 1,
    iconDownOffset: { x: 0, y: 4 },
    fontDownOffset: { x: 0, y: 4 },
    fontOffset: { x: 0, y: 0 },
    iconOffset: { x: 0, y: 0 },
    frameOnDown: 1,
    frameOnUp: 0,
    tint: 0xFFFFFF,
};
var ComponentButton = /** @class */ (function (_super) {
    __extends(ComponentButton, _super);
    function ComponentButton(properties) {
        var _this = _super.call(this) || this;
        _this.soundDown = sfx_generalbutton;
        _this.isDown = false;
        _this.name = "Button";
        // Initialize properties
        _this.initialize(tgfSettings.componentDefaults.button);
        _this.touchStart = new tbVector(0, 0);
        // Set user properties
        if (properties !== undefined) {
            _this.setProperties(properties);
        }
        return _this;
    }
    // Returns the width of the component. Must be overriden if it is not the width property
    ComponentButton.prototype.getWidth = function () {
        return this.width;
    };
    // Returns the height of the component. Must be overriden if it is not the width property
    ComponentButton.prototype.getHeight = function () {
        return this.height;
    };
    // ***************************************
    // Internal methods
    // ***************************************
    // Returns the actual text
    ComponentButton.prototype.findText = function () {
        var text = this.text, key = this.dictionaryKey;
        // Find the correct value for toggle buttons
        if (Object.prototype.toString.call(this.text) === "[object Array]") {
            text = this.text[this.toggleIndex % this.text.length];
        }
        if (Object.prototype.toString.call(this.dictionaryKey) === "[object Array]") {
            key = this.dictionaryKey[this.toggleIndex % this.dictionaryKey.length];
        }
        // Now determine the actual text
        if (key !== "") {
            // TODO: fix the dictionary
            text = tgfDictionary.getText(key);
        }
        return text;
    };
    ComponentButton.prototype.drawIcon = function () {
        var xx, yy, image = this.icon;
        if (!image) {
            return;
        }
        if (this._iconCreated) {
            image = this.icon;
        }
        else {
            image = this.icon.clone();
            this._iconCreated = true;
        }
        // Determine the position
        switch (this.iconPosition) {
            case "left":
                xx = this.marginLeft;
                yy = (this.marginTop - this.marginBottom + this.image.height - image.height) / 2;
                break;
            case "center":
                xx = (this.image.width - image.width) / 2;
                yy = (this.marginTop - this.marginBottom + this.image.height - image.height) / 2;
                break;
            case "right":
                xx = this.image.width - image.width - this.marginRight;
                yy = (this.marginTop - this.marginBottom + this.image.height - image.height) / 2;
                break;
            case "top":
                xx = (this.image.width - image.width) / 2;
                yy = this.marginTop;
                break;
            case "bottom":
                xx = (this.image.width - image.width) / 2;
                yy = this.image.width - image.width - this.marginBottom;
                break;
            default: console.error("No case for iconPosition with value", this.iconPosition, this);
        }
        // Giving the icons an optional offset
        if (this.iconOffset) {
            xx += this.iconOffset.x;
            yy += this.iconOffset.y;
        }
        if (this.roundPosition) {
            xx = Math.floor(xx);
            yy = Math.floor(yy);
        }
        image.setPosition(xx + image.xOrigin, yy + image.yOrigin);
        image.setScale(this.iconUpScale, this.iconUpScale);
        this.image.addChild(image);
        image.setAlpha(this.iconAlpha);
        this.icon = image;
    };
    // Draws the text in the correct way at position x, y
    ComponentButton.prototype.drawText = function () {
        var xx, yy, ww, hh, oldSize, newSize, font, fnt, text = this.findText();
        if (text === "") {
            return;
        }
        if (this.fontDown) {
            font = this.fontDown;
        }
        else {
            font = this.fontUp;
        }
        if (font instanceof tbVectorFont) {
            fnt = font.clone();
        }
        else {
            fnt = new tbVectorFont();
            fnt.setProperties(font);
        }
        fnt.setText(text);
        this._font = fnt;
        // Determine the box for the text
        xx = this.marginLeft;
        yy = this.marginTop;
        ww = this.width - this.marginLeft - this.marginRight;
        hh = this.height - this.marginTop - this.marginBottom;
        switch (fnt.align) {
            case "left": break;
            case "center":
                xx += ww / 2;
                break;
            case "right":
                xx += ww;
                break;
        }
        switch (fnt.baseline) {
            case "top": break;
            case "middle":
                yy += hh / 2;
                break;
            case "bottom":
                yy += hh;
                break;
        }
        if (this.roundPosition) {
            xx = Math.floor(xx);
            yy = Math.floor(yy);
        }
        if (this.splitLines && this.width) {
            fnt.setWordWrap(this.splitLines, ww);
        }
        xx += this.fontOffset.x;
        yy += this.fontOffset.y;
        // Compute the size
        oldSize = fnt.getFontSize();
        // Optionally fit the text to the bounds of the button
        if (this.fitToBox) {
            var height = this.height;
            if (height <= 0) {
                height = hh;
            }
            newSize = fnt.textFitSize(text, ww, height, this.splitLines);
            if (newSize < oldSize) {
                fnt.setFontSize(newSize);
            }
        }
        this.image.addChild(fnt);
        fnt.setPosition(xx, yy);
        this.fontText = fnt;
    };
    ComponentButton.prototype.setText = function (text) {
        this.text = text ? text : this.text;
        this.fontText.setText(this.text);
    };
    // Set the state of the button to up or down
    ComponentButton.prototype._setStateUp = function () {
        if (!this.isActive || !this.isVisible || !this.canClick || !this.isDown) {
            return;
        }
        this.finger = -1;
        if (this.soundUp) {
            tgfAudio.play(this.soundUp);
        }
        if (this.onClick) {
            this.onClick();
        }
    };
    // Set the state of the button to up or down
    ComponentButton.prototype._setStateDown = function () {
        if (!this.isActive || !this.isVisible || !this.canClick) {
            return;
        }
        this.isDown = true;
        if (this.soundUp) {
            tgfAudio.play(this.soundUp);
        }
        if (this.onClick) {
            this.onClick();
        }
    };
    ComponentButton.prototype._resetButtonState = function () {
        var _a, _b;
        this.canClick = false;
        this.isDown = false;
        this.changeImageFrame(0);
        (_a = this.icon) === null || _a === void 0 ? void 0 : _a.setScale(this.iconUpScale, this.iconUpScale);
        (_b = this.fontText) === null || _b === void 0 ? void 0 : _b.setScale(this.fontUpScale, this.fontUpScale);
    };
    ComponentButton.prototype._iconDown = function () {
        if (this.icon) {
            this._iconPositionX = this._iconPositionX || this.icon.x;
            this._iconPositionY = this._iconPositionY || this.icon.y;
            this.icon.setScale(this.iconDownScale, this.iconDownScale);
            this.icon.setPosition(this._iconPositionX + this.iconDownOffset.x, this._iconPositionY + this.iconDownOffset.y);
        }
        if (this.fontText) {
            this._fontPositionX = this._fontPositionX || this.fontText.x;
            this._fontPositionY = this._fontPositionY || this.fontText.y;
            this.fontText.setScale(this.fontDownScale, this.fontDownScale);
            this.fontText.setPosition(this._fontPositionX + this.fontDownOffset.x, this._fontPositionY + this.fontDownOffset.y);
        }
    };
    ComponentButton.prototype._iconUp = function () {
        if (this.icon) {
            this._iconPositionX = this._iconPositionX || this.icon.x;
            this._iconPositionY = this._iconPositionY || this.icon.y;
            this.icon.setScale(this.iconUpScale, this.iconUpScale);
            this.icon.setPosition(this._iconPositionX, this._iconPositionY);
        }
        if (this.fontText) {
            this._fontPositionX = this._fontPositionX || this.fontText.x;
            this._fontPositionY = this._fontPositionY || this.fontText.y;
            this.fontText.setScale(this.fontUpScale, this.fontUpScale);
            this.fontText.setPosition(this._fontPositionX, this._fontPositionY);
        }
    };
    // ***************************************
    // Preparing, showing and activating
    // ***************************************
    // Prepare the component for use; returns whether already prepared
    ComponentButton.prototype.doPrepare = function () {
        this.toggleCount = 2;
        var nineSliceOffsetX = this.nineSliceOffsetX || 0;
        var nineSliceOffsetY = this.nineSliceOffsetY || 0;
        this._imageCollection = [];
        this.image = this.image.clone();
        this.image.setTint(this.tint);
        for (var j = 0; j < this.toggleCount; j++) {
            this._imageCollection.push(this.image.getTexture(this.image.getFrame(j)));
        }
        if (!this.createFromSlices) {
            this.image.sprite = this.image.getFrame(0);
        }
        else {
            this._imageNineSlices = [];
            this._imageNineSlices[0] = this.image.getNineSlice(this.frameOnUp, Math.floor(this.image.width / 2) - 2 + nineSliceOffsetX, Math.floor(this.image.width / 2) - 2 - nineSliceOffsetX, Math.floor(this.image.height / 2) - 2 + nineSliceOffsetY, Math.floor(this.image.height / 2) - 2 - nineSliceOffsetY);
            this._imageNineSlices[1] = this.image.getNineSlice(this.frameOnDown, Math.floor(this.image.width / 2) - 2 + nineSliceOffsetX, Math.floor(this.image.width / 2) - 2 - nineSliceOffsetX, Math.floor(this.image.height / 2) - 2 + nineSliceOffsetY, Math.floor(this.image.height / 2) - 2 - nineSliceOffsetY);
            this.image = s_tgf_transparent.clone();
            this.image.setDimensions(this.width, this.height);
            for (var s = 0; s < this._imageNineSlices.length; s++) {
                if (!this._imageNineSlices[s]) {
                    throw new Error("Failed to get nine slice for button " + this.name);
                }
                this._imageNineSlices[s].setDimensions(this.width, this.height);
                this._imageNineSlices[s].add(this.image);
                if (s > 0) {
                    this._imageNineSlices[s].setAlpha(0);
                }
            }
        }
        if (this.width <= 0) {
            this.width = this.image.width;
        }
        if (this.height <= 0) {
            this.height = this.image.height;
        }
        this.image.setAlpha(this.alpha);
        if (this.mask) {
            this.image.setMask(this.mask);
        }
        this.image.setCallback(this._upCallback.bind(this), false);
        this.image.setCallback(this._downCallback.bind(this), true);
        this.image.cancelCallback(this._cancelCallback.bind(this));
        this.drawText();
        this.drawIcon();
        // Add this image to the baseContainer
        this.image.add(this.baseContainer);
        this.finger = -1;
        // Add the child components
        for (var i = 0; i < this.components.length; i += 1) {
            this.addComponent(this.components[i]);
        }
        // Set touchMarginBox from touchMargin if not set already
        if (this.touchMargin && !this.touchMarginBox) {
            this.touchMarginBox = [this.touchMargin, this.touchMargin, this.touchMargin, this.touchMargin];
        }
        if (this.touchMarginBox && this.touchMarginBox.length === 4) {
            this.image["sprite"]["hitArea"] = new PIXI["Rectangle"](0 - this.touchMarginBox[0], 0 - this.touchMarginBox[1], this.image["sprite"]["width"] + this.touchMarginBox[0] + this.touchMarginBox[2], this.image["sprite"]["height"] + this.touchMarginBox[1] + this.touchMarginBox[3]);
        }
    };
    ComponentButton.prototype._upCallback = function () {
        if (!this.isActive || !this.isVisible || !this.canClick || !this.isDown) {
            this.isDown = false;
            return false;
        }
        if (!this.reactOnDown) {
            this._setStateUp();
        }
        this.isDown = false;
        this._iconUp();
        this.changeImageFrame(this.frameOnUp);
        return true;
    };
    ComponentButton.prototype._downCallback = function () {
        if (!this.isActive || !this.isVisible || !this.canClick) {
            return false;
        }
        this.isDown = true;
        if (this.reactOnDown) {
            this._setStateDown();
        }
        this._iconDown();
        this.changeImageFrame(this.frameOnDown);
        return true;
    };
    ComponentButton.prototype._cancelCallback = function () {
        this._resetButtonState();
    };
    // Processing required when freeing the component after use
    ComponentButton.prototype.changeImageFrame = function (frame) {
        if (!this.image) {
            return;
        }
        if (this.createFromSlices) {
            // Clamp between 1 and 0 so the correct nine slice can be found
            if (frame === this.frameOnDown) {
                frame = 1;
            }
            else if (frame === this.frameOnUp) {
                frame = 0;
            }
            for (var s = 0; s < this._imageNineSlices.length; s++) {
                if (!this._imageNineSlices[s]) {
                    continue;
                }
                if (s === frame) {
                    this._imageNineSlices[s].setAlpha(1);
                }
                else {
                    this._imageNineSlices[s].setAlpha(0);
                }
            }
        }
        else {
            this.image.changeFrame(this._imageCollection[frame]);
        }
    };
    ComponentButton.prototype.changeIcon = function (icon) {
        // recreating icon, so set to false
        this._iconCreated = false;
        this.image.removeChild(this.icon);
        this.icon = icon;
        this.drawIcon();
    };
    ComponentButton.prototype.bringToFront = function () {
        this.image.remove(this.baseContainer);
        this.image.add(this.baseContainer);
    };
    ComponentButton.prototype.doFree = function () {
        if (this.icon && this.image) {
            this.icon.remove(this.image);
            this.icon.free();
            this.icon = null;
        }
        if (this.fontText && this.image) {
            this.fontText.remove(this.image);
            this.fontText.free();
            this.fontText = null;
        }
        if (this.image) {
            this.image.remove(this.baseContainer);
            this.image.free();
            this.image = null;
        }
        if (this._imageNineSlices) {
            for (var i = 0; i < this._imageNineSlices.length; i++) {
                this._imageNineSlices[i].free();
                this._imageNineSlices[i] = null;
            }
            this._imageNineSlices = [];
        }
    };
    // ***************************************
    // Methods called when events must be processed
    // ***************************************
    // Called when a touch is started or if a mouse button is pressed
    ComponentButton.prototype.doTouchDown = function (finger, x, y) {
        this.finger = finger;
        this.touchStart.x = tbGame.touchX(this.finger);
        this.touchStart.y = tbGame.touchY(this.finger);
    };
    // Called when a touch is ended or if a mouse button is released
    ComponentButton.prototype.doTouchUp = function (finger, x, y) {
        this.finger = -1;
        this.canClick = true;
    };
    // Called if the key with the given keyCode is pressed
    ComponentButton.prototype.doKeyDown = function (keyCode) {
        if (this.anyKey || (this.shortcutKey && keyCode === this.shortcutKey)) {
            if (this.cancelEvents) {
                return true;
            }
        }
    };
    // Called if the key with the given keyCode is released
    ComponentButton.prototype.keyCode = function (keyCode) {
        if (this.anyKey || (this.shortcutKey && keyCode === this.shortcutKey)) {
            if (this.cancelEvents) {
                return true;
            }
        }
    };
    // Called at the end of each step
    ComponentButton.prototype.doEndStep = function (timePassed) {
        // Check whether the finger moves on or off the button
        if (this.finger !== -1) {
            var xx = tbGame.touchX(this.finger);
            var yy = tbGame.touchY(this.finger);
            var dist = tbMath.distance(this.touchStart.x, this.touchStart.y, xx, yy);
            if (dist >= this.allowedTouchMotion) {
                this._resetButtonState();
                this.finger = -1;
            }
        }
    };
    // Called when the component must be drawn (relative to position 0, 0)
    ComponentButton.prototype.doDraw = function () {
        var ww = this.width, hh = this.height, xx = -ww / 2, yy = -hh / 2;
        if (this.roundPosition) {
            xx = Math.floor(xx);
            yy = Math.floor(yy);
        }
        this.image.setPosition(xx + this.containerRelativeX(), yy + this.containerRelativeY());
        this.image.setScale(this.xScale, this.yScale);
    };
    ComponentButton.prototype.setAlpha = function (alpha) {
        var _a;
        this.alpha = alpha;
        (_a = this.image) === null || _a === void 0 ? void 0 : _a.setAlpha(this.alpha);
    };
    ComponentButton.prototype.setIconAlpha = function (alpha) {
        var _a;
        (_a = this.icon) === null || _a === void 0 ? void 0 : _a.setAlpha(alpha);
    };
    ComponentButton.prototype.setTextAlpha = function (alpha) {
        var _a;
        (_a = this.fontText) === null || _a === void 0 ? void 0 : _a.setAlpha(alpha);
    };
    ComponentButton.prototype.doHide = function () {
        if (this.image) {
            this.image.hide();
        }
        if (this.icon) {
            this.icon.hide();
        }
        if (this.fontText) {
            this.fontText.hide();
        }
    };
    ComponentButton.prototype.doShow = function () {
        if (this.image) {
            this.image.show();
        }
        if (this.icon) {
            this.icon.show();
        }
        if (this.fontText) {
            this.fontText.show();
        }
    };
    ComponentButton.prototype.doDeactivate = function () {
        this._resetButtonState();
    };
    ComponentButton.prototype.doActivate = function () {
        this.canClick = true;
    };
    ComponentButton.prototype.setTint = function (tint) {
        this.tint = tint;
        this.image.setTint(tint);
    };
    return ComponentButton;
}(ComponentBase));
export { ComponentButton };
